import { useTurnaroundList } from 'api/hooks/useTurnaroundList';
import { useLanguage } from 'context/AppContext';
import { useTurnaroundListFilters } from '../hooks/useTurnaroundListFilters';
import {
  Table,
  TableCell,
  TableRow,
  TableHead,
  TableBody,
  Skeleton,
  styled,
  Typography,
  Button,
  TableContainer,
  TablePagination,
  Box,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { TurnaroundListItem } from 'api/models/TurnaroundList';
import theme from 'App.theme';
import { HighlightOff, PendingOutlined, TaskAlt, Visibility } from '@mui/icons-material';
import { formatDate } from 'utils/helpers/DateTimeFormat.helpers';
import en from 'locale/en';
import { TurnaroundSet } from 'components/TurnaroundSet/TurnaroundSet';
import { TurnaroundImplants } from 'components/TurnaroundImplants/TurnaroundImplants';
import { useNavigate } from 'react-router';
import routes from 'routes';
import { useCancelTurnaround } from 'api/hooks/useCancelTurnaround';
import { OptionDialog } from 'components/OptionDialog/OptionDialog';
import { toast } from 'react-toastify';
import { getCurrentPage } from 'utils/getCurrentPage';
import Pagination from 'components/Pagination';
import { getTotalPages } from 'utils/getTotalPages';
import { trackEvent } from 'analytics';
import { EventType } from 'analytics/models';
import { useCurrentUser } from 'api/hooks/useCurrentUser';

const DEFAULT_PAGINATION_LIMIT = 100;
const ICON_SIZE = 64;
const ACTIONS_ICON_SIZE = 32;

const StyledCell = styled(TableCell)({
  padding: theme.layout.spacing.small,
});

const CellContainer = styled('div')({
  height: '100%',
  border: `1px solid ${theme.greys.GREYA2}`,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.layout.spacing.tiny,
  padding: theme.layout.spacing.tiny,
});

const TEXT_ELLIPSIS_STYLES = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  width: '100%',
};

const STATUS_TEXT_STYLES = {
  lineHeight: '1.2',
  textAlign: 'center',
  ...TEXT_ELLIPSIS_STYLES,
};

const StatusCell = ({
  turnaroundStage,
  turnaroundTimestamp,
  turnaroundBy,
}: {
  turnaroundStage: TurnaroundListItem['turnaroundStage'];
  turnaroundTimestamp: TurnaroundListItem['turnaroundTimestamp'];
  turnaroundBy: TurnaroundListItem['turnaroundBy'];
}) => {
  return (
    <CellContainer
      sx={{
        justifyContent: 'center',
        alignItems: 'center',
        color:
          turnaroundStage === 'COMPLETED'
            ? theme.colors.anotherGreen
            : turnaroundStage === 'PENDING'
            ? theme.colors.amber
            : '',
      }}
    >
      <div>
        {turnaroundStage === 'COMPLETED' ? (
          <TaskAlt sx={{ width: ICON_SIZE, height: ICON_SIZE }} />
        ) : (
          <PendingOutlined sx={{ width: ICON_SIZE, height: ICON_SIZE }} />
        )}
      </div>

      <Typography variant="caption" sx={STATUS_TEXT_STYLES}>
        {turnaroundStage}
      </Typography>

      <Typography variant="caption" sx={STATUS_TEXT_STYLES}>
        {turnaroundTimestamp ? formatDate(turnaroundTimestamp, true) : ''}
      </Typography>

      <Typography variant="caption" sx={STATUS_TEXT_STYLES}>
        {turnaroundBy}
      </Typography>
    </CellContainer>
  );
};

const SurgeryCell = ({
  variant,
  lang,
  supplier,
  surgeon,
  surgeryDate,
  surgeryId,
  orderNumber,
  deliveryNumber,
}: {
  variant: 'origin' | 'destination';
  lang: typeof en.TurnaroundList.surgeryDetails;
  supplier: TurnaroundListItem['surgeryOrigin']['supplier'];
  surgeon: TurnaroundListItem['surgeryOrigin']['surgeon'];
  surgeryDate: TurnaroundListItem['surgeryOrigin']['surgeryDate'];
  surgeryId: TurnaroundListItem['surgeryOrigin']['surgeryId'];
  orderNumber: TurnaroundListItem['surgeryOrigin']['orderNumber'];
  deliveryNumber: TurnaroundListItem['surgeryOrigin']['deliveryNumber'];
}) => {
  const rows = [
    { label: lang.supplier, value: supplier },
    { label: lang.surgeon, value: surgeon },
    { label: lang.surgeryDate, value: formatDate(surgeryDate) },
    { label: lang.surgeryId, value: surgeryId },
    { label: lang.orderNumber, value: orderNumber },
    { label: lang.deliveryNumber, value: deliveryNumber },
  ];

  return (
    <CellContainer
      sx={{
        backgroundColor: variant === 'origin' ? theme.colors.primaryLight : theme.colors.amberLight,
      }}
    >
      {rows.map(({ label, value }) => (
        <Typography key={label} variant="body2" sx={{ ...TEXT_ELLIPSIS_STYLES }}>
          {label}: <b>{value}</b>
        </Typography>
      ))}
    </CellContainer>
  );
};

const TurnaroundItems = ({
  sets,
  implants,
}: {
  sets: TurnaroundListItem['sets'];
  implants: TurnaroundListItem['implants'];
}) => {
  return (
    <CellContainer sx={{ maxHeight: 175, overflow: 'auto', padding: 0 }}>
      {sets?.map(set => (
        <TurnaroundSet key={set.kitInstanceId} set={set} disabled />
      ))}

      {implants ? <TurnaroundImplants disabled /> : null}
    </CellContainer>
  );
};

const ViewButton = ({
  destinationDeliveryId,
  destinationDeliveryStatus,
}: {
  destinationDeliveryId: TurnaroundListItem['surgeryDestination']['deliveryId'];
  destinationDeliveryStatus: TurnaroundListItem['surgeryDestination']['deliveryStatus'];
}) => {
  const lang = useLanguage()[0].TurnaroundList;

  const navigate = useNavigate();

  return (
    <Button
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textTransform: 'none',
        color: theme.colors.active,
      }}
      onClick={() =>
        navigate(
          // We need to know if we should go to check in or check out
          destinationDeliveryStatus === 'RECEIVED' ||
            destinationDeliveryStatus === 'PARTIALLY_CHECKED_IN' ||
            destinationDeliveryStatus === 'OVERDUE_FOR_CHECK_IN'
            ? routes.CheckIn.makePath(destinationDeliveryId)
            : routes.CheckOut.makePath(destinationDeliveryId),
        )
      }
    >
      <Visibility sx={{ width: ACTIONS_ICON_SIZE, height: ACTIONS_ICON_SIZE, color: 'inherit' }} />

      {lang.view}
    </Button>
  );
};

const CancelButton = ({
  originSurgery,
  destinationSurgery,
  sets,
  hasImplants,
}: {
  originSurgery: TurnaroundListItem['surgeryOrigin'];
  destinationSurgery: TurnaroundListItem['surgeryDestination'];
  sets: TurnaroundListItem['sets'];
  hasImplants: boolean;
}) => {
  const lang = useLanguage()[0].TurnaroundList;

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const { data: currentUser } = useCurrentUser();

  const { mutateAsync: cancelTurnaround, isLoading: cancelTurnaroundLoading } = useCancelTurnaround();

  return (
    <>
      <Button
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textTransform: 'none',
          color: theme.colors.red,
        }}
        onClick={() => setConfirmDialogOpen(true)}
      >
        <HighlightOff sx={{ width: ACTIONS_ICON_SIZE, height: ACTIONS_ICON_SIZE, color: 'inherit' }} />

        {lang.cancel}
      </Button>

      <OptionDialog
        open={confirmDialogOpen}
        title={lang.cancelConfirmationTitle}
        confirmDisabled={cancelTurnaroundLoading}
        confirmLoading={cancelTurnaroundLoading}
        onClose={() => setConfirmDialogOpen(false)}
        onCancel={() => setConfirmDialogOpen(false)}
        onConfirm={async () => {
          if (!currentUser) {
            throw new Error('Current user is not defined');
          }

          await cancelTurnaround({
            deliveryId: originSurgery.deliveryId,
            kitInstanceIds: sets?.map(set => set.kitInstanceId) ?? [],
            cancelImplants: hasImplants,
          });

          await trackEvent(EventType.TurnaroundCancel, {
            current_user: currentUser.userId,
            tenant_name: currentUser.tenantName,
            origin_supplier: originSurgery.supplier || '',
            origin_surgeon: originSurgery.surgeon || '',
            origin_surgery_date: originSurgery.surgeryDate ? formatDate(originSurgery?.surgeryDate) : '',
            origin_surgery_id: originSurgery.surgeryId || '',
            origin_order_number: originSurgery.orderNumber || '',
            origin_delivery_number: originSurgery.deliveryId.toString(),
            destination_supplier: destinationSurgery.supplier || '',
            destination_surgeon: destinationSurgery.surgeon || '',
            destination_surgery_date: destinationSurgery.surgeryDate ? formatDate(destinationSurgery?.surgeryDate) : '',
            destination_surgery_id: destinationSurgery.surgeryId || '',
            destination_order_number: destinationSurgery.orderNumber || '',
            destination_delivery_number: destinationSurgery.deliveryId.toString(),
            set_ids: sets?.map(set => set.kitInstanceId) ?? [],
            has_implants: hasImplants,
          });

          setConfirmDialogOpen(false);

          toast.success(lang.cancelSuccess);
        }}
      />
    </>
  );
};

const ActionsCell = ({
  turnaroundStage,
  originSurgery,
  destinationSurgery,
  sets,
  hasImplants,
}: {
  turnaroundStage: TurnaroundListItem['turnaroundStage'];
  originSurgery: TurnaroundListItem['surgeryOrigin'];
  destinationSurgery: TurnaroundListItem['surgeryDestination'];
  sets: TurnaroundListItem['sets'];
  hasImplants: boolean;
}) => {
  return (
    <CellContainer sx={{ justifyContent: 'center', alignItems: 'center' }}>
      <ViewButton
        destinationDeliveryId={destinationSurgery.deliveryId}
        destinationDeliveryStatus={destinationSurgery.deliveryStatus}
      />

      {turnaroundStage === 'PENDING' && (
        <CancelButton
          originSurgery={originSurgery}
          destinationSurgery={destinationSurgery}
          sets={sets}
          hasImplants={hasImplants}
        />
      )}
    </CellContainer>
  );
};

type Column = {
  id: string;
  label: string;
  style?: React.CSSProperties;
};

export const TurnaroundList = () => {
  const lang = useLanguage()[0].TurnaroundList;

  const [filters, setFilters] = useTurnaroundListFilters();
  const { data, isLoading } = useTurnaroundList(filters);

  const rows = data?.result;
  const totalRows = data?.metadata.total || 0;
  const currentPage = data ? getCurrentPage({ offset: data.metadata.offset, limit: data.metadata.limit }) : 1;
  const currentPageIndex = currentPage - 1;
  const rowsPerPage = data?.metadata.limit || DEFAULT_PAGINATION_LIMIT;
  const totalPages = data ? getTotalPages({ total: totalRows, limit: rowsPerPage }) : 1;

  const columns = useMemo<Column[]>(() => {
    return [
      { id: 'status', label: lang.columns.status, style: { maxWidth: 140 } },
      { id: 'surgeryOrigin', label: lang.columns.surgeryOrigin, style: { maxWidth: 280 } },
      { id: 'itemsTurnedAround', label: lang.columns.itemsTurnedAround, style: { maxWidth: 280 } },
      { id: 'surgeryDestination', label: lang.columns.surgeryDestination, style: { maxWidth: 280 } },
      { id: 'actions', label: lang.columns.actions },
    ];
  }, [lang]);

  const renderCell = useCallback(
    (item: TurnaroundListItem, columnId: Column['id']) => {
      switch (columnId) {
        case 'status':
          return (
            <StatusCell
              turnaroundStage={item.turnaroundStage}
              turnaroundTimestamp={item.turnaroundTimestamp}
              turnaroundBy={item.turnaroundBy}
            />
          );
        case 'surgeryOrigin':
          return <SurgeryCell variant="origin" lang={lang.surgeryDetails} {...item.surgeryOrigin} />;
        case 'itemsTurnedAround':
          return <TurnaroundItems sets={item.sets} implants={item.implants} />;
        case 'surgeryDestination':
          return <SurgeryCell variant="destination" lang={lang.surgeryDetails} {...item.surgeryDestination} />;
        case 'actions':
          return (
            <ActionsCell
              turnaroundStage={item.turnaroundStage}
              originSurgery={item.surgeryOrigin}
              destinationSurgery={item.surgeryDestination}
              sets={item.sets}
              hasImplants={Boolean(item.implants)}
            />
          );
        default:
          return null;
      }
    },
    [lang],
  );

  const handleChangePage = useCallback(
    (_, newPage: number) => {
      if (!data) return;

      const offset = newPage * data.metadata.limit;

      setFilters({ ...filters, offset: offset.toString(), limit: data.metadata.limit.toString() });
    },
    [data, filters, setFilters],
  );

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const rowsPerPage = parseInt(event.target.value, 10);

      setFilters({ ...filters, offset: '0', limit: rowsPerPage.toString() });
    },
    [filters, setFilters],
  );

  return (
    <TableContainer sx={{ position: 'relative', height: '100%' }}>
      <Table
        stickyHeader
        sx={{
          height: '1px', // hack to make the table cells expand to the height of the row
          borderCollapse: 'separate',
          borderSpacing: `0 ${theme.layout.spacing.small}`,
          border: 'transparent',
        }}
      >
        <TableHead>
          <TableRow sx={{ backgroundColor: theme.greys.GREYFA }}>
            {columns.map(column => (
              <StyledCell key={column.id} sx={{ textAlign: 'center', ...column.style }}>
                {column.label}
              </StyledCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {isLoading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <TableRow key={index}>
                {columns.map(column => (
                  <StyledCell key={column.id} sx={{ ...column.style }}>
                    <Skeleton />
                  </StyledCell>
                ))}
              </TableRow>
            ))
          ) : rows?.length ? (
            rows.map(item => (
              <TableRow
                key={item.turnaroundTimestamp}
                sx={{
                  backgroundColor: theme.colors.white,
                }}
              >
                {columns.map(column => (
                  <StyledCell key={column.id} sx={{ ...column.style }}>
                    {renderCell(item, column.id)}
                  </StyledCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <StyledCell colSpan={columns.length}>{lang.noResults}</StyledCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          right: 0,
          left: 0,
          backgroundColor: theme.colors.white,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: theme.layout.spacing.small,
        }}
      >
        <Pagination
          numPages={totalPages}
          curPage={currentPage}
          disabled={isLoading}
          onChange={page => handleChangePage(null, page - 1)}
          sx={{ height: 52, display: 'flex', alignItems: 'center', margin: 0 }}
        />

        <TablePagination
          component="div"
          count={totalRows}
          rowsPerPage={rowsPerPage}
          page={currentPageIndex}
          disabled={isLoading}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          // Hide the back and next buttons (they're already present in the Pagination component)
          slotProps={{
            actions: {
              nextButton: { style: { display: 'none' } },
              previousButton: { style: { display: 'none' } },
            },
          }}
        />
      </Box>
    </TableContainer>
  );
};
